import React, { useState, useEffect } from "react";
import {
  ChevronDown,
  ChevronUp,
  Package,
  ShoppingCart,
  Tag,
  Truck,
  MapPin,
  Info,
  Phone,
  Weight,
  Box,
  CreditCard,
} from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

// Define color scheme
const COLORS = {
  orange: "#E87E04",
  white: "#FFFFFF",
  green: "#008751",
  darkGreen: "#006B3F",
  lightOrange: "#FFE0B2",
  lightGreen: "#CCFFCC",
  gray: "#718096",
  lightGray: "#E2E8F0",
  paleGreen: "#E6FFEA",
};

const OrderedItems = ({ items, totalPrice }) => {
  const [expandedGroups, setExpandedGroups] = useState({});
  const [groupedArticles, setGroupedArticles] = useState({});
  const [articles, setArticles] = useState([]);
  const [reduction, setReduction] = useState(0);
  const [shippingDetails, setShippingDetails] = useState(null);

  useEffect(() => {
    const panierItems = items?.prod || [];
    const detecterRegion = async () => {
      try {
        const region = items?.livraisonDetails?.region || null;
        const shipping = items?.shippingDetails || null;

        // Set shipping details if available
        if (shipping) {
          setShippingDetails(shipping);
        }

        // Group articles and calculate shipping fees
        const grouped = groupArticles(panierItems);
        Object.keys(grouped).forEach((productId) => {
          const shippingFees = calculateGroupShipping(
            grouped[productId],
            region
          );
          grouped[productId] = {
            ...grouped[productId],
            ...shippingFees,
          };
        });

        setGroupedArticles(grouped);
        setArticles(panierItems);
        calculerTotal();
      } catch (error) {
        console.error("Erreur de détection de région", error);
        const grouped = groupArticles(panierItems);
        setGroupedArticles(grouped);
        setArticles(panierItems);
        calculerTotal();
      }
    };

    detecterRegion();
  }, [items]);

  // Group articles function
  const groupArticles = (articlesArray) => {
    return articlesArray.reduce((groups, article) => {
      const productId = article._id;
      if (!groups[productId]) {
        groups[productId] = {
          productId,
          name: article.name,
          imageUrl: article.imageUrl,
          shipping: article.shipping,
          variants: [],
          totalQuantity: 0,
          baseShippingFee: 0,
          weightShippingFee: 0,
        };
      }

      groups[productId].variants.push(article);
      groups[productId].totalQuantity += article.quantity;
      return groups;
    }, {});
  };

  // Calculate shipping fees for a group
  const calculateGroupShipping = (group, region) => {
    const shippingInfo = group.shipping;
    if (
      !shippingInfo ||
      !shippingInfo.zones ||
      shippingInfo.zones.length === 0
    ) {
      return {
        baseShippingFee: 1000,
        weightShippingFee: 0,
      };
    }

    let zoneClient = shippingInfo.zones.find(
      (zone) => zone.name.toLowerCase() === region?.toLowerCase()
    );

    if (!zoneClient && shippingInfo.zones.length > 0) {
      zoneClient = shippingInfo.zones[0];
    }

    if (zoneClient) {
      const baseShippingFee = zoneClient.baseFee || 0;
      let totalWeightFee = 0;

      // Calculate weight fees for each variant
      group.variants.forEach((variant) => {
        const weightFee = shippingInfo.weight
          ? shippingInfo.weight * (zoneClient.weightFee || 0) * variant.quantity
          : 0;
        totalWeightFee += weightFee;
      });

      return {
        baseShippingFee,
        weightShippingFee: totalWeightFee,
      };
    }

    return {
      baseShippingFee: 1000,
      weightShippingFee: 0,
    };
  };

  const calculerTotalFraisExpedition = () => {
    return Object.values(groupedArticles).reduce((total, group) => {
      return total + group.baseShippingFee + group.weightShippingFee;
    }, 0);
  };

  const calculerSousTotal = () => {
    return articles.reduce(
      (total, article) => total + article.price * article.quantity,
      0
    );
  };

  const calculerTotal = () => {
    const sousTotal = calculerSousTotal();
    const totalAvecReduction = sousTotal - reduction;
    const totalFinal = totalAvecReduction + calculerTotalFraisExpedition();

    return totalFinal;
  };

  // Toggle group expansion
  const toggleGroupExpansion = (productId) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [productId]: !prev[productId],
    }));
  };

  // Render a product group
  const renderArticleGroup = (group) => {
    const isExpanded = expandedGroups[group.productId];
    const totalShippingFee = group.baseShippingFee + group.weightShippingFee;

    return (
      <Card
        key={group.productId}
        className="mb-4 border border-lightGray overflow-hidden transition-all duration-200 hover:shadow-md"
      >
        {/* Group header */}
        <div
          className="p-4 border-b cursor-pointer group transition-all duration-200 hover:bg-paleGreen"
          onClick={() => toggleGroupExpansion(group.productId)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="relative w-16 h-16 rounded-md overflow-hidden border border-lightGray">
                <img
                  src={group.imageUrl}
                  alt={group.name}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                />
              </div>
              <div>
                <h3 className="font-medium text-lg text-darkGreen">
                  {group.name}
                </h3>
                <Badge variant="outline" className="bg-paleGreen text-green">
                  {group.variants.length} variante(s)
                </Badge>
              </div>
            </div>
            <div className="text-orange">
              {isExpanded ? (
                <ChevronUp className="w-6 h-6 transition-transform duration-300" />
              ) : (
                <ChevronDown className="w-6 h-6 transition-transform duration-300" />
              )}
            </div>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-2">
            <div className="flex items-center text-sm text-gray col-span-2">
              <Package className="h-4 w-4 mr-2 text-orange" />
              <span>Quantité totale: {group.totalQuantity}</span>
            </div>
          </div>
        </div>

        {/* Variants list */}
        {isExpanded && (
          <CardContent className="p-4 space-y-4 bg-white">
            {group?.variants?.map((variant, index) => (
              <div
                key={`${variant._id}-${index}`}
                className="flex items-center justify-between border-b pb-4 last:border-0"
              >
                <div className="flex items-center">
                  {/* Variant image if available */}
                  {variant?.imageUrl && (
                    <div className="w-12 h-12 rounded-md overflow-hidden border border-lightGray mr-3">
                      <img
                        src={variant.imageUrl}
                        alt={`Couleur ${variant.colors[0]}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="flex flex-col">
                    <div className="flex flex-wrap gap-2 mb-1">
                      {variant.colors[0] && (
                        <Badge
                          variant="outline"
                          className="bg-lightOrange text-orange border-orange"
                        >
                          Couleur: {variant.colors[0]}
                        </Badge>
                      )}
                      {variant.sizes[0] && (
                        <Badge
                          variant="outline"
                          className="bg-lightGreen text-green border-green"
                        >
                          Taille: {variant.sizes[0]}
                        </Badge>
                      )}
                    </div>
                    <div className="text-sm text-gray">
                      Quantité:{" "}
                      <span className="font-medium text-green">
                        {variant.quantity}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="text-orange font-medium text-lg">
                  {(() => {
                    // Détermine le prix normal (on prend d'abord le prix de la variante si disponible)
                    const vari = variant.variants.find(
                      (item) => item.color === variant.colors[0]
                    );

                    const normalPrice =
                      vari?.price && vari.price > 0
                        ? vari?.price
                        : variant.prix || 0;

                    const discountPrice =
                      vari?.promoPrice && vari.promoPrice > 0
                        ? vari?.promoPrice
                        : variant.prixPromo || 0;

                    // Détermine le prix promo (on prend d'abord le prix promo de la variante si disponible)
                    // const discountPrice =
                    //   variant.promoPrice || variant.prixPromo || 0;

                    // Vérifie si le produit est en promotion
                    const isOnPromo =
                      (discountPrice > 0 && discountPrice < normalPrice) ||
                      variant.isOnPromo;

                    if (isOnPromo) {
                      return (
                        <>
                          <div className="line-through text-red-500 font-medium ml-3">
                            {normalPrice} €
                          </div>
                          <div className="text-[#008751] font-medium ml-3">
                            {discountPrice} €
                          </div>
                        </>
                      );
                    } else {
                      return (
                        <div className="text-[#008751] font-medium ml-3">
                          {normalPrice} €
                        </div>
                      );
                    }
                  })()}
                </div>
              </div>
            ))}
          </CardContent>
        )}
      </Card>
    );
  };

  // Render shipping details section
  const renderShippingDetails = () => {
    if (!shippingDetails) return null;

    return (
      <Card className="mb-6 border-green border-l-4">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold flex items-center text-darkGreen">
            <Truck className="mr-2 h-5 w-5" />
            Détails d'expédition
          </CardTitle>
        </CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-4 pt-0">
          <div className="space-y-3">
            <div className="flex items-start">
              <MapPin className="h-5 w-5 mr-2 text-green flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-darkGreen">Zone de livraison</p>
                <p className="text-gray">{shippingDetails.zoneName}</p>
              </div>
            </div>

            <div className="flex items-start">
              <Package className="h-5 w-5 mr-2 text-green flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-darkGreen">Transporteur</p>
                <p className="text-gray">
                  {shippingDetails.transporteur.nom} (ID:{" "}
                  {shippingDetails.transporteur.id})
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <Phone className="h-5 w-5 mr-2 text-green flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-darkGreen">Contact</p>
                <p className="text-gray">
                  {shippingDetails.transporteur.contact}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-start">
              <Weight className="h-5 w-5 mr-2 text-green flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-darkGreen">Poids total</p>
                <p className="text-gray">{shippingDetails.poidsTotal}</p>
              </div>
            </div>

            <div className="flex items-start">
              <Box className="h-5 w-5 mr-2 text-green flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-darkGreen">Volume total</p>
                <p className="text-gray">{shippingDetails.volumeTotal}</p>
              </div>
            </div>
          </div>

          <Separator className="col-span-2 my-2" />

          <div className="col-span-2">
            <div className="bg-paleGreen rounded-lg p-3">
              <h4 className="font-medium text-darkGreen mb-2 flex items-center">
                <CreditCard className="h-4 w-4 mr-2" />
                Frais d'expédition
              </h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray">Frais de base:</span>
                  <span className="font-medium text-green">
                    {shippingDetails.fraisBase} €
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray">Frais de poids:</span>
                  <span className="font-medium text-green">
                    {shippingDetails.fraisPoids} €
                  </span>
                </div>
                <div className="col-span-2 flex justify-between border-t pt-2 mt-1">
                  <span className="font-medium text-darkGreen">
                    Coût total d'expédition:
                  </span>
                  <span className="font-bold text-orange">
                    {shippingDetails.coutTotal} €
                  </span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Render articles section
  const renderArticlesSection = () => {
    if (Object.keys(groupedArticles).length === 0) {
      return (
        <Alert variant="default" className="bg-paleGreen border-green">
          <ShoppingCart className="h-5 w-5 text-green" />
          <AlertTitle className="text-darkGreen">Panier vide</AlertTitle>
          <AlertDescription className="text-gray">
            Vous n'avez pas encore ajouté d'articles à votre commande.
          </AlertDescription>
        </Alert>
      );
    }

    return (
      <div className="space-y-4">
        {Object.values(groupedArticles).map((group) =>
          renderArticleGroup(group)
        )}
      </div>
    );
  };

  // Calculate summary values
  const sousTotal = calculerSousTotal();
  const fraisExpedition = calculerTotalFraisExpedition();
  const totalFinal = calculerTotal();

  return (
    <div className="space-y-6">
      {/* Shipping details section */}
      {renderShippingDetails()}

      {/* Ordered items section */}
      <div>
        <h2 className="font-semibold text-lg mb-4 text-darkGreen flex items-center">
          <Package className="mr-2 h-5 w-5" />
          Articles commandés
        </h2>

        {renderArticlesSection()}
      </div>

      {/* Order summary section */}
      <Card className="border-orange border-t-4 bg-white">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold text-orange">
            Résumé de la commande
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-gray">Sous-total</span>
              <span className="font-medium">
                {(totalPrice || totalFinal) - (shippingDetails?.coutTotal || 0)}{" "}
                €
              </span>
            </div>

            {reduction > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-gray">Réduction</span>
                <span className="font-medium text-green">-{reduction} €</span>
              </div>
            )}

            <div className="flex justify-between items-center">
              <span className="text-gray">Frais d'expédition</span>
              <span className="font-medium">
                {" "}
                {shippingDetails?.coutTotal} €
              </span>
            </div>

            <Separator className="my-2" />

            <div className="flex justify-between items-center pt-2">
              <span className="text-lg font-medium text-darkGreen">Total</span>
              <span className="text-xl font-bold text-orange">
                {totalPrice || totalFinal} €
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OrderedItems;